/**
 * GroupDashboardsTab — embeds the group's canonical assurance dashboard in the hub.
 */
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import Loading from 'src/components/ui/Loading';
import Panel from '@/components/ui/Panel';
import TitledPanel from '@/components/ui/TitledPanel';
import { useGroupDashboards } from 'src/features/reporting/api/hooks';
import GroupDrilldown from 'src/features/reporting/components/GroupDrilldown';

export function groupDashboardsTabPath(groupId: string, openNodeId?: string | null): string {
  const path = `/groups/${groupId}/dashboards`;
  if (!openNodeId) return path;
  return `${path}?openNode=${encodeURIComponent(openNodeId)}`;
}

interface Props {
  groupId: string;
  cardId?: string;
}

export default function GroupDashboardsTab({ groupId, cardId }: Props) {
  const navigate = useTenantNavigate();
  const { data, isLoading, error, refetch } = useGroupDashboards(groupId);
  const dashboard = data?.nodes[0] ?? null;

  if (isLoading) {
    return (
      <Panel>
        <Loading text="Loading dashboard..." />
      </Panel>
    );
  }

  if (error) {
    return (
      <TitledPanel title="Dashboards">
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">Failed to load the group dashboard.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      </TitledPanel>
    );
  }

  if (!dashboard) {
    return (
      <TitledPanel title="Dashboards">
        <p className="text-sm text-content-subtle">
          This group does not have an assurance dashboard yet. A contributor or admin can create one by opening this
          tab.
        </p>
      </TitledPanel>
    );
  }

  return (
    <Panel>
      <GroupDrilldown
        dashboardId={dashboard.id}
        nestedNodeId={cardId}
        shell="plain"
        pagerShowReportingHome={false}
        rootParentPath={null}
        getNodePath={(_dashId, nodeId) =>
          nodeId ? `/groups/${groupId}/dashboards/${nodeId}` : `/groups/${groupId}/dashboards`
        }
        onRootDeleted={() => navigate(`/groups/${groupId}/dashboards`)}
      />
    </Panel>
  );
}
